import React, { useEffect, useMemo, useState } from 'react';
import config from '../config';
import './OperationsManager.css';

const emptyVehicle = {
  id: null,
  immatriculation: '',
  marque: '',
  modele: '',
  categorie: 'Utilitaire',
  statut: 'disponible',
  personne_id: '',
  kilometrage: 0,
  controle_technique: '',
  assurance_echeance: '',
  notes: '',
};

const statusLabels = {
  disponible: 'Disponible',
  affecte: 'Affecté',
  entretien: 'En entretien',
  hors_service: 'Hors service',
};

function formatDate(value) {
  if (!value) return 'Non renseignée';
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR');
}

function initials(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
}

export default function OperationsManager({ view }) {
  const [team, setTeam] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [vehicle, setVehicle] = useState(emptyVehicle);

  const token = localStorage.getItem(config.tokenKey) || '';

  async function request(action, options = {}) {
    const response = await fetch(`${config.apiBase}/operations.php?action=${encodeURIComponent(action)}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    const text = await response.text();
    let data;
    try { data = text ? JSON.parse(text) : {}; } catch { data = { message: text }; }
    if (!response.ok || data.success === false) throw new Error(data.message || `Erreur HTTP ${response.status}`);
    return data;
  }

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const teamData = await request('team_list');
      setTeam(Array.isArray(teamData.team) ? teamData.team : []);
      if (view === 'vehicles') {
        const vehicleData = await request('vehicles_list');
        setVehicles(Array.isArray(vehicleData.vehicles) ? vehicleData.vehicles : []);
      }
    } catch (err) {
      setError(err.message || 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, [view]);

  const filteredTeam = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return team;
    return team.filter(person => `${person.nom || ''} ${person.username || ''} ${person.fonction || ''} ${person.role || ''}`.toLowerCase().includes(needle));
  }, [team, query]);

  const filteredVehicles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return vehicles;
    return vehicles.filter(item => `${item.immatriculation} ${item.marque} ${item.modele || ''} ${item.categorie || ''} ${item.personne_nom || ''}`.toLowerCase().includes(needle));
  }, [vehicles, query]);

  function editVehicle(item) {
    setVehicle({
      ...emptyVehicle,
      ...item,
      personne_id: item.personne_id || '',
      controle_technique: item.controle_technique || '',
      assurance_echeance: item.assurance_echeance || '',
    });
    setShowForm(true);
  }

  async function saveVehicle(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await request('vehicle_save', { method: 'POST', body: JSON.stringify({ action: 'vehicle_save', ...vehicle }) });
      setShowForm(false);
      setVehicle(emptyVehicle);
      await loadData();
    } catch (err) {
      setError(err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }

  async function deleteVehicle(item) {
    if (!window.confirm(`Supprimer le véhicule ${item.immatriculation} ?`)) return;
    setError('');
    try {
      await request('vehicle_delete', { method: 'POST', body: JSON.stringify({ action: 'vehicle_delete', id: item.id }) });
      await loadData();
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
    }
  }

  const activeCount = team.filter(person => person.personne_id).length;
  const availableCount = vehicles.filter(item => item.statut === 'disponible').length;
  const maintenanceCount = vehicles.filter(item => item.statut === 'entretien').length;

  return (
    <main className="operations-page">
      <header className="operations-heading">
        <div>
          <span className="operations-kicker">Gestion opérationnelle</span>
          <h1>{view === 'team' ? 'Équipe' : 'Parc de véhicules'}</h1>
          <p>{view === 'team' ? 'Annuaire des collaborateurs actifs du planning Isolacier.' : 'Affectations, kilométrage et échéances du parc.'}</p>
        </div>
        {view === 'vehicles' && (
          <button className="operations-primary" onClick={() => { setVehicle(emptyVehicle); setShowForm(true); }}>
            Ajouter un véhicule
          </button>
        )}
      </header>

      <section className="operations-stats">
        {view === 'team' ? (
          <>
            <article><span>Comptes</span><strong>{team.length}</strong></article>
            <article><span>Dans le planning</span><strong>{activeCount}</strong></article>
            <article><span>Administrateurs</span><strong>{team.filter(person => ['admin', 'superadmin'].includes(String(person.role).toLowerCase())).length}</strong></article>
          </>
        ) : (
          <>
            <article><span>Véhicules</span><strong>{vehicles.length}</strong></article>
            <article><span>Disponibles</span><strong>{availableCount}</strong></article>
            <article><span>En entretien</span><strong>{maintenanceCount}</strong></article>
          </>
        )}
      </section>

      <section className="operations-panel">
        <div className="operations-toolbar">
          <div>
            <strong>{view === 'team' ? 'Collaborateurs' : 'Véhicules enregistrés'}</strong>
            <span>{view === 'team' ? filteredTeam.length : filteredVehicles.length} résultat(s)</span>
          </div>
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Rechercher…" />
        </div>

        {error && <div className="operations-error">{error}</div>}
        {loading && <div className="operations-empty">Chargement…</div>}

        {!loading && view === 'team' && (
          <div className="team-grid">
            {filteredTeam.map(person => {
              const displayName = person.nom || person.username;
              return (
                <article className="team-card" key={person.user_id}>
                  <span className="team-avatar" style={{ background: person.couleur || '#e32636' }}>{initials(displayName)}</span>
                  <div className="team-info">
                    <strong>{displayName}</strong>
                    <span>{person.fonction || 'Fonction non renseignée'}</span>
                    <small>@{person.username}</small>
                  </div>
                  <span className={`role-badge role-${person.role}`}>{person.role === 'superadmin' ? 'Super admin' : person.role === 'admin' ? 'Admin' : 'Technicien'}</span>
                </article>
              );
            })}
            {filteredTeam.length === 0 && <div className="operations-empty">Aucun collaborateur trouvé.</div>}
          </div>
        )}

        {!loading && view === 'vehicles' && (
          <div className="vehicle-grid">
            {filteredVehicles.map(item => (
              <article className="vehicle-card" key={item.id}>
                <div className="vehicle-card-head">
                  <div><strong>{item.immatriculation}</strong><span>{item.marque} {item.modele}</span></div>
                  <span className={`vehicle-status status-${item.statut}`}>{statusLabels[item.statut] || item.statut}</span>
                </div>
                <dl>
                  <div><dt>Catégorie</dt><dd>{item.categorie || '—'}</dd></div>
                  <div><dt>Affecté à</dt><dd>{item.personne_nom || 'Personne'}</dd></div>
                  <div><dt>Kilométrage</dt><dd>{Number(item.kilometrage || 0).toLocaleString('fr-FR')} km</dd></div>
                  <div><dt>Contrôle technique</dt><dd>{formatDate(item.controle_technique)}</dd></div>
                  <div><dt>Assurance</dt><dd>{formatDate(item.assurance_echeance)}</dd></div>
                </dl>
                {item.notes && <p className="vehicle-notes">{item.notes}</p>}
                <div className="vehicle-actions">
                  <button onClick={() => editVehicle(item)}>Modifier</button>
                  <button className="danger" onClick={() => deleteVehicle(item)}>Supprimer</button>
                </div>
              </article>
            ))}
            {filteredVehicles.length === 0 && <div className="operations-empty">Aucun véhicule enregistré.</div>}
          </div>
        )}
      </section>

      {showForm && (
        <div className="vehicle-modal-overlay" onMouseDown={event => event.target === event.currentTarget && setShowForm(false)}>
          <section className="vehicle-modal" role="dialog" aria-modal="true" aria-label="Véhicule">
            <header><div><span>Parc Isolacier</span><h2>{vehicle.id ? 'Modifier le véhicule' : 'Nouveau véhicule'}</h2></div><button onClick={() => setShowForm(false)} aria-label="Fermer">×</button></header>
            <form onSubmit={saveVehicle}>
              <div className="vehicle-form-grid">
                <label>Immatriculation *<input value={vehicle.immatriculation} onChange={event => setVehicle({ ...vehicle, immatriculation: event.target.value })} required /></label>
                <label>Marque *<input value={vehicle.marque} onChange={event => setVehicle({ ...vehicle, marque: event.target.value })} required /></label>
                <label>Modèle<input value={vehicle.modele} onChange={event => setVehicle({ ...vehicle, modele: event.target.value })} /></label>
                <label>Catégorie<input value={vehicle.categorie} onChange={event => setVehicle({ ...vehicle, categorie: event.target.value })} /></label>
                <label>Statut<select value={vehicle.statut} onChange={event => setVehicle({ ...vehicle, statut: event.target.value })}>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                <label>Affecté à<select value={vehicle.personne_id} onChange={event => setVehicle({ ...vehicle, personne_id: event.target.value })}><option value="">Personne</option>{team.filter(person => person.personne_id).map(person => <option key={person.personne_id} value={person.personne_id}>{person.nom || person.username}</option>)}</select></label>
                <label>Kilométrage<input type="number" min="0" value={vehicle.kilometrage} onChange={event => setVehicle({ ...vehicle, kilometrage: event.target.value })} /></label>
                <label>Contrôle technique<input type="date" value={vehicle.controle_technique} onChange={event => setVehicle({ ...vehicle, controle_technique: event.target.value })} /></label>
                <label>Échéance assurance<input type="date" value={vehicle.assurance_echeance} onChange={event => setVehicle({ ...vehicle, assurance_echeance: event.target.value })} /></label>
              </div>
              <label className="vehicle-notes-field">Notes<textarea rows="4" value={vehicle.notes} onChange={event => setVehicle({ ...vehicle, notes: event.target.value })} /></label>
              <footer><button type="button" onClick={() => setShowForm(false)}>Annuler</button><button className="operations-primary" disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button></footer>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}
